import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import {
  Card,
  Spin,
  Tag,
  Typography,
  Button
} from 'antd';
import {
  ShopOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  ExpandOutlined
} from '@ant-design/icons';
import partnerService from '../services/partnerService';
import { formatCityCountry } from '../utils/locationFormat';
import 'leaflet/dist/leaflet.css';
import './PartnerMapPreview.scss';

const { Title, Text, Paragraph } = Typography;

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Single marker icon for all partners (Animap green color)
const partnerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const PartnerMapPreview = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Greece center coordinates (default)
  const defaultCenter = [39.0742, 21.8243];
  const defaultZoom = 6;

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    setLoading(true);
    const result = await partnerService.fetchPartners();

    if (result.success) {
      const partnersList = result.data.results || result.data || [];
      setPartners(partnersList);
    }
    setLoading(false);
  };

  // Filter partners to only include those with coordinates
  const partnersWithCoordinates = useMemo(() => {
    return partners.filter(partner => partner.latitude && partner.longitude);
  }, [partners]);

  // Calculate map center based on partners
  const mapCenter = useMemo(() => {
    if (partnersWithCoordinates.length === 0) {
      return defaultCenter;
    }

    const lats = partnersWithCoordinates.map(p => parseFloat(p.latitude));
    const lngs = partnersWithCoordinates.map(p => parseFloat(p.longitude));

    const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;
    const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

    return [avgLat, avgLng];
  }, [partnersWithCoordinates]);


  const handleViewPartner = (partnerId) => {
    navigate(`/partners/${partnerId}`);
  };

  if (loading) {
    return (
      <section className="partner-map-preview-section">
        <div className="sweet-container">
          <div className="loading-container" style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="large" />
          </div>
        </div>
      </section>
    );
  }

  if (partnersWithCoordinates.length === 0) {
    return null; // Don't show the section if there are no partners with coordinates
  }

  return (
    <section className="partner-map-preview-section">
      <div className="sweet-container">
        <div className="section-header">
          <div>
            <Title level={2} className="section-title">
              <EnvironmentOutlined style={{ marginRight: 12, color: '#9FB18C' }} />
              {t('home.partnerMap.title')}
            </Title>
            <Paragraph className="section-description">
              {t('home.partnerMap.description')}
            </Paragraph>
          </div>
          <Button
            type="primary"
            icon={<ExpandOutlined />}
            onClick={() => navigate('/partners/map')}
          >
            {t('home.partnerMap.viewFullMap')}
          </Button>
        </div>

        {/* Map */}
        <Card className="map-card" bodyStyle={{ padding: 0 }}>
          <MapContainer
            center={mapCenter}
            zoom={defaultZoom}
            style={{ height: 550, width: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {partnersWithCoordinates.map(partner => (
              <Marker
                key={partner.id}
                position={[parseFloat(partner.latitude), parseFloat(partner.longitude)]}
                icon={partnerIcon}
              >
                <Popup>
                  <div className="partner-popup">
                    <div className="popup-header">
                      {partner.logo ? (
                        <img
                          src={partner.logo}
                          alt={partner.store_name}
                          className="popup-logo"
                        />
                      ) : (
                        <div className="popup-logo-placeholder">
                          <ShopOutlined />
                        </div>
                      )}
                      <div className="popup-title">
                        <strong>{partner.store_name}</strong>
                        <div className="popup-tags">
                          {partner.is_verified && (
                            <Tag color="green" size="small">
                              <SafetyCertificateOutlined /> {t('partners.verified')}
                            </Tag>
                          )}
                          {partner.is_impact_partner && (
                            <Tag color="magenta" size="small">
                              🤝 {t('partners.impact')}
                            </Tag>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="popup-location">
                      <EnvironmentOutlined />
                      <span>{formatCityCountry(partner.city, partner.country, i18n.language)}</span>
                    </div>
                    {partner.service_categories && partner.service_categories.length > 0 && (
                      <div className="popup-categories">
                        {partner.service_categories.slice(0, 3).map((cat, idx) => (
                          <Tag key={idx} color="blue" size="small">
                            {i18n.language === 'el' ? (cat.name_el || cat.name_en) : cat.name_en}
                          </Tag>
                        ))}
                      </div>
                    )}
                    <Button
                      type="primary"
                      size="small"
                      block
                      onClick={() => handleViewPartner(partner.id)}
                      style={{ marginTop: 8 }}
                    >
                      {t('partners.viewProfile')}
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Card>
      </div>
    </section>
  );
};

export default PartnerMapPreview;
